import { inkKey, type CanonicalDocument, type Color } from '@besbello/design-core';
import type { Diagnostic } from '../types.js';
import { ruleIdOf, type ValidationContext } from '../rules.js';

/** Todas las tintas declaradas en el documento, deduplicadas por identidad. */
export function collectInks(doc: CanonicalDocument): { key: string; color: Color }[] {
  const map = new Map<string, Color>();
  for (const layer of doc.layers) {
    if (layer.kind === 'text') map.set(inkKey(layer.color), layer.color);
    if (layer.kind === 'shape') {
      if (layer.fill) map.set(inkKey(layer.fill), layer.fill);
      if (layer.stroke) map.set(inkKey(layer.stroke), layer.stroke);
    }
  }
  return [...map.entries()].map(([key, color]) => ({ key, color })).sort((a, b) => (a.key < b.key ? -1 : 1));
}

/**
 * DEC-07 — La serigrafia se produce por TINTAS PLANAS SEPARADAS. Contar tintas
 * no es un detalle estetico: cada tinta es una pantalla, un fotolito y una
 * pasada. Por eso el limite es un dato del proceso y el exceso es un error.
 */
export function validateInks(doc: CanonicalDocument, ctx: ValidationContext): Diagnostic[] {
  const out: Diagnostic[] = [];
  const ruleId = ruleIdOf(ctx.technique);
  const inks = collectInks(doc);

  // Una imagen de tono continuo no es separable en tintas planas.
  const hasContinuousTone = doc.layers.some((l) => l.kind === 'image');
  if (hasContinuousTone && !ctx.technique.gradientsAllowed) {
    for (const layer of doc.layers) {
      if (layer.kind !== 'image') continue;
      out.push({
        code: 'GRADIENT_NOT_ALLOWED',
        severity: 'error',
        layerId: layer.id,
        unit: 'none',
        ruleId,
        params: { technique: ctx.technique.techniqueId },
      });
    }
  }

  if (ctx.technique.maxInks !== null) {
    // Una imagen de tono continuo cuenta como cuatricromia a efectos de tintas.
    const inkCount = inks.length + (hasContinuousTone ? 4 : 0);
    if (inkCount > ctx.technique.maxInks) {
      out.push({
        code: 'TOO_MANY_INKS',
        severity: 'error',
        measured: inkCount,
        expected: ctx.technique.maxInks,
        unit: 'count',
        ruleId,
        params: { inks: inks.map((i) => i.key).join(', ') },
      });
    }
  }

  for (const { key, color } of inks) {
    if (!ctx.technique.allowedColorTypes.includes(color.type)) {
      out.push({
        code: 'COLOR_TYPE_NOT_SUPPORTED',
        severity: 'error',
        unit: 'none',
        ruleId,
        params: { ink: key, colorType: color.type },
      });
    }
    if (color.type === 'technical_white' && !ctx.technique.technicalWhiteAvailable) {
      out.push({
        code: 'TECHNICAL_WHITE_NOT_AVAILABLE',
        severity: 'error',
        unit: 'none',
        ruleId,
        params: { technique: ctx.technique.techniqueId },
      });
    }
  }

  return out;
}

/**
 * Cuenta de tintas para el PRECIO: es la misma cifra que valida el proceso, y
 * eso es deliberado. Si el precio contase de otra forma, un pedido valido
 * podria costar lo que no es.
 */
export function countInksForPricing(doc: CanonicalDocument): number {
  const inks = collectInks(doc);
  const hasContinuousTone = doc.layers.some((l) => l.kind === 'image');
  return inks.length + (hasContinuousTone ? 4 : 0);
}
